import React, { useState } from 'react'
import { DayPickerRangeController, CalendarDay, isInclusivelyAfterDay } from 'react-dates'
import { useTranslation } from 'react-i18next'
import { css, CSSObject, SerializedStyles } from '@emotion/core'
import moment from 'moment'
import 'react-dates/initialize'

type DateRangeFieldProps = {
  beginDate: string | undefined
  endDate: string | undefined
  changeRangeDate: (beginDate, endDate) => void
  customCss?: CSSObject | SerializedStyles
  minimumDate?: string // 最小日付（YYYY-MM-DD形式）
  disableEndDate?: boolean // チェックアウト日の選択を無効化
}

export const DateRangeFixedCalender: React.FC<DateRangeFieldProps> = ({
  beginDate,
  endDate,
  customCss,
  changeRangeDate,
  minimumDate,
  disableEndDate = false,
}) => {
  const { t } = useTranslation()
  const [focusedInput, setFocusedInput] = useState<'startDate' | 'endDate'>('startDate')

  const _onClose = ({ endDate }) => {
    // After selecting only the starting point, reset if you leave
    if (endDate === null) {
      changeRangeDate('', '')
    }
  }

  const _onChange = event => {
    // In the start point selection, always reset the end point when changing the date
    if (focusedInput === 'startDate') {
      changeRangeDate(event.startDate?.format('YYYY-MM-DD'), '')
      return
    }
    changeRangeDate(event.startDate?.format('YYYY-MM-DD'), event.endDate?.format('YYYY-MM-DD'))
  }

  const _onFocusChange = focusedInput => {
    // チェックアウト日が無効化されている場合は、常にstartDateに固定
    if (disableEndDate) {
      setFocusedInput('startDate')
      return
    }
    setFocusedInput(focusedInput !== null ? focusedInput : 'startDate')
  }

  // 最小日付より前の日付を選択不可にする
  const isOutsideRange = (day: moment.Moment) => {
    if (minimumDate) {
      return !isInclusivelyAfterDay(day, moment(minimumDate).subtract(1, 'days'))
    }
    return false
  }

  return (
    <div css={[dateRangeControllerStyle, customCss]}>
      <DayPickerRangeController
        initialVisibleMonth={() => (beginDate ? moment(beginDate) : moment())}
        startDate={beginDate ? moment(beginDate) : null}
        endDate={disableEndDate ? null : endDate ? moment(endDate) : null}
        focusedInput={disableEndDate ? 'startDate' : focusedInput}
        onDatesChange={_onChange}
        onFocusChange={_onFocusChange}
        onClose={_onClose}
        onOutsideClick={() => {}}
        numberOfMonths={1}
        minimumNights={0}
        monthFormat={t('MMM-YY')}
        hideKeyboardShortcutsPanel={true}
        daySize={42}
        isOutsideRange={isOutsideRange}
        enableOutsideDays
        navPrev={
          <div className="monthPrev">
            <img
              alt="arrow"
              src={require('@/static/images/icon_caalender_left.svg')}
              onMouseOver={e => (e.currentTarget.src = require('@/static/images/icon_caalender_left.svg'))}
              onMouseOut={e => (e.currentTarget.src = require('@/static/images/icon_caalender_left.svg'))}
            />
          </div>
        }
        navNext={
          <div className="monthNext">
            <img
              alt="arrow"
              src={require('@/static/images/icon_caalender_right.svg')}
              onMouseOver={e => (e.currentTarget.src = require('@/static/images/icon_caalender_right.svg'))}
              onMouseOut={e => (e.currentTarget.src = require('@/static/images/icon_caalender_right.svg'))}
            />
          </div>
        }
        renderCalendarDay={props => (
          <CalendarDay
            {...props}
            renderDayContents={day => (
              <div className="day">
                <span>{day?.format('D')}</span>
              </div>
            )}
          />
        )}
      />
    </div>
  )
}

const dateRangeControllerStyle = css({
  '.DayPicker': {
    boxShadow: 'initial',
  },
  '.CalendarDay__outside': {
    color: '#cccccc',
  },
})
